import { type Option } from '~/components/Select';
import { countries } from '~/constants/countries';
import { B13AFilingOptionMap } from '~/constants/customsDeclaration';

import { isMilitaryAddress, isUSTerritory } from './Address';
import { type WebCustomsItem } from './CustomsItem';

type JSONCustomsDeclarationExporterIdentificationTaxId = {
  number: string;
  type: string;
};

type JSONCustomsDeclarationExporterIdentification = {
  eori_number: string;
  tax_id: JSONCustomsDeclarationExporterIdentificationTaxId;
};

type JSONCustomsDeclaration = {
  aes_itn: string;
  b13a_filing_option: string;
  b13a_number: string;
  certificate: string;
  certify_signer: string;
  contents_explanation: string;
  contents_type: string;
  disclaimer: string;
  eel_pfc: string;
  exporter_identification: JSONCustomsDeclarationExporterIdentification;
  exporter_reference: string;
  id: number;
  importer_reference: string;
  incoterm: string;
  invoice: string;
  is_certify: boolean;
  is_commercial_invoice: boolean;
  is_test: boolean;
  license: string;
  metadata: string;
  non_delivery_option: string;
  notes: string;
  object_state: string;
};

type CustomsDeclaration = {
  aesItn?: string;
  b13aFilingOption?: string;
  b13aNumber?: string;
  certificate: string;
  certifySigner: string;
  contentsExplanation?: string;
  contentsType: string;
  disclaimer?: string;
  eelPfc: string;
  eoriNumber?: string;
  exporterReference: string;
  id: number;
  importerReference: string;
  incoterm: string;
  invoice: string;
  isCertify: boolean;
  isCommercialInvoice?: boolean;
  isTest?: boolean;
  license: string;
  metadata?: string;
  nonDeliveryOption: string;
  notes: string;
  objectState?: string;
  taxIdNumber?: string;
  taxIdType?: string;
};

type CustomsDeclarationPayload = {
  aesItn?: string;
  b13aFilingOption?: Option;
  b13aNumber: string;
  certificate: string;
  certifySigner: string;
  contentsExplanation?: string;
  contentsType: Option;
  eelPfc: Option;
  eoriNumber: string;
  exporterReference: string;
  id: number;
  importerReference: string;
  incoterm: Option;
  invoice: string;
  isCertify: boolean;
  license: string;
  nonDeliveryOption: Option;
  notes: string;
  taxIdNumber: string;
  taxIdType: Option;
};

type JSONCustomsDeclarationPayload = {
  aes_itn?: string;
  b13a_filing_option: string;
  b13a_number: string;
  certificate: string;
  certify_signer: string;
  contents_explanation: string;
  contents_type: string;
  eel_pfc: string;
  eori_number: string;
  exporter_reference: string;
  id: number;
  importer_reference: string;
  incoterm: string;
  invoice: string;
  is_certify: boolean;
  license: string;
  non_delivery_option: string;
  notes: string;
  tax_id_number: string;
  tax_id_type: string;
};

type PIILessJSONCustomsDeclarationPayload = {
  aes_itn?: boolean;
  b13a_filing_option: string;
  b13a_number: boolean;
  certificate: boolean;
  certify_signer: boolean;
  contents_explanation: string;
  contents_type: string;
  eel_pfc: string;
  eori_number: boolean;
  exporter_reference: boolean;
  id: number;
  importer_reference: boolean;
  incoterm: string;
  invoice: boolean;
  is_certify: boolean;
  license: string;
  non_delivery_option: string;
  notes: string;
  tax_id_number: boolean;
  tax_id_type: string;
};

export type WebCustomsDeclaration = {
  /** Automated Export System Internal Transaction Number (exports from USA) */
  aes_itn: string;
  /** Name of the person who created the customs declaration and is responsible for the validity of all information provided */
  authorized_person: string;
  /** B13A Export Declaration, mandatory for Canadian exports valued at CAD$2000 or more */
  b13a_filing_option: 'FILED_ELECTRONICALLY' | 'NOT_REQUIRED' | 'SUMMARY_REPORTING';
  /** Proof of Report Number, Summary ID Number, or Exemption Number, depending on B13A filing option */
  b13a_number: string;
  certificate: string;
  /** Exemption and Exclusion Legend / Proof of Filing Citation (exports from USA) */
  eel_pfc: 'AES_ITN' | 'NOEEI_30_36' | 'NOEEI_30_37_a' | 'NOEEI_30_37_f' | 'NOEEI_30_37_h';
  eori_number: string;
  exporter_reference: string;
  importer_reference: string;
  incoterm: 'DDP' | 'DDU' | 'FCA';
  invoice_number: string;
  /** Expresses that the certify_signer has provided all information of this customs declaration truthfully */
  is_certified: boolean;
  is_vat_collected: boolean;
  items: WebCustomsItem[];
  license: string;
  metadata: string;
  non_delivery_handling: 'ABANDON' | 'RETURN';
  notes: string;
  /** Explanation of the type of goods of the shipment */
  purpose_explanation: string;
  /** Reason for export */
  purpose_of_shipment:
    | 'DOCUMENTS'
    | 'GIFT'
    | 'HUMANITARIAN_DONATION'
    | 'MERCHANDISE'
    | 'OTHER'
    | 'RETURN_MERCHANDISE'
    | 'SAMPLE';
  tax_id: string;
  tax_id_type: string;
};

const toJSONCustomsDeclarationPayload = (
  customsDeclarationPayload: CustomsDeclarationPayload,
): JSONCustomsDeclarationPayload => ({
  aes_itn: customsDeclarationPayload.aesItn ? customsDeclarationPayload.aesItn : '',
  b13a_filing_option: (customsDeclarationPayload.b13aFilingOption?.value as string) || '',
  b13a_number: customsDeclarationPayload.b13aNumber || '',
  certificate: customsDeclarationPayload.certificate,
  certify_signer: customsDeclarationPayload.certifySigner,
  contents_explanation: customsDeclarationPayload.contentsExplanation
    ? customsDeclarationPayload.contentsExplanation
    : '',
  contents_type: customsDeclarationPayload.contentsType.value as string,
  eel_pfc: customsDeclarationPayload.eelPfc
    ? (customsDeclarationPayload.eelPfc.value as string)
    : '',
  eori_number: customsDeclarationPayload.eoriNumber || '',
  exporter_reference: customsDeclarationPayload.exporterReference,
  id: customsDeclarationPayload.id,
  importer_reference: customsDeclarationPayload.importerReference,
  incoterm: customsDeclarationPayload.incoterm.value as string,
  invoice: customsDeclarationPayload.invoice,
  is_certify: customsDeclarationPayload.isCertify,
  license: customsDeclarationPayload.license,
  non_delivery_option: customsDeclarationPayload.nonDeliveryOption.value as string,
  notes: customsDeclarationPayload.notes,
  tax_id_number: customsDeclarationPayload.taxIdNumber || '',
  tax_id_type: customsDeclarationPayload.taxIdType
    ? (customsDeclarationPayload.taxIdType?.value as string)
    : '',
});

const prepareCustomsDeclarationSegmentData = (
  customsDeclarationPayload: JSONCustomsDeclarationPayload,
): PIILessJSONCustomsDeclarationPayload => ({
  ...customsDeclarationPayload,
  aes_itn: !!customsDeclarationPayload.aes_itn,
  b13a_number: !!customsDeclarationPayload.b13a_number,
  certificate: !!customsDeclarationPayload.certificate,
  certify_signer: !!customsDeclarationPayload.certify_signer,
  eori_number: !!customsDeclarationPayload.eori_number,
  exporter_reference: !!customsDeclarationPayload.exporter_reference,
  importer_reference: !!customsDeclarationPayload.importer_reference,
  invoice: !!customsDeclarationPayload.invoice,
  tax_id_number: !!customsDeclarationPayload.tax_id_number,
});

const fromJSON = (jsonCustomsDeclaration: JSONCustomsDeclaration): CustomsDeclaration => ({
  aesItn: jsonCustomsDeclaration.aes_itn,
  b13aFilingOption: B13AFilingOptionMap[jsonCustomsDeclaration.b13a_filing_option],
  b13aNumber: jsonCustomsDeclaration.b13a_number,
  certificate: jsonCustomsDeclaration.certificate,
  certifySigner: jsonCustomsDeclaration.certify_signer,
  contentsExplanation: jsonCustomsDeclaration.contents_explanation,
  contentsType: jsonCustomsDeclaration.contents_type,
  disclaimer: jsonCustomsDeclaration.disclaimer,
  eelPfc: jsonCustomsDeclaration.eel_pfc,
  eoriNumber: jsonCustomsDeclaration.exporter_identification.eori_number,
  exporterReference: jsonCustomsDeclaration.exporter_reference,
  id: jsonCustomsDeclaration.id,
  importerReference: jsonCustomsDeclaration.importer_reference,
  incoterm: jsonCustomsDeclaration.incoterm,
  invoice: jsonCustomsDeclaration.invoice,
  isCertify: jsonCustomsDeclaration.is_certify,
  isCommercialInvoice: jsonCustomsDeclaration.is_commercial_invoice,
  isTest: jsonCustomsDeclaration.is_test,
  license: jsonCustomsDeclaration.license,
  metadata: jsonCustomsDeclaration.metadata,
  nonDeliveryOption: jsonCustomsDeclaration.non_delivery_option,
  notes: jsonCustomsDeclaration.notes,
  objectState: jsonCustomsDeclaration.object_state,
  taxIdNumber: jsonCustomsDeclaration.exporter_identification.tax_id.number,
  taxIdType: jsonCustomsDeclaration.exporter_identification.tax_id.type,
});

export const fromWebCustomsDeclaration = (
  webCustomsDeclaration: WebCustomsDeclaration,
): CustomsDeclaration => ({
  aesItn: webCustomsDeclaration.aes_itn,
  b13aFilingOption: webCustomsDeclaration.b13a_filing_option,
  b13aNumber: webCustomsDeclaration.b13a_number,
  certificate: webCustomsDeclaration.certificate,
  certifySigner: '',
  contentsExplanation: webCustomsDeclaration.purpose_explanation,
  contentsType: webCustomsDeclaration.purpose_of_shipment,
  disclaimer: undefined,
  eelPfc: webCustomsDeclaration.eel_pfc,
  eoriNumber: webCustomsDeclaration.eori_number,
  exporterReference: webCustomsDeclaration.exporter_reference,
  //nulls
  id: -1,
  importerReference: webCustomsDeclaration.importer_reference,
  incoterm: webCustomsDeclaration.incoterm,
  invoice: webCustomsDeclaration.invoice_number,
  isCertify: webCustomsDeclaration.is_certified,
  isCommercialInvoice: false,
  isTest: false,
  license: webCustomsDeclaration.license,

  metadata: webCustomsDeclaration.metadata,
  nonDeliveryOption: webCustomsDeclaration.non_delivery_handling,
  notes: webCustomsDeclaration.notes,
  objectState: undefined,
  taxIdNumber: webCustomsDeclaration.tax_id,
  taxIdType: webCustomsDeclaration.tax_id_type,
});

const isDeclarationEqual = (a: CustomsDeclaration, b: CustomsDeclaration) =>
  a.contentsType === b.contentsType &&
  a.contentsExplanation === b.contentsExplanation &&
  a.exporterReference === b.exporterReference &&
  a.importerReference === b.importerReference &&
  a.invoice === b.invoice &&
  a.license === b.license &&
  a.certificate === b.certificate &&
  a.eelPfc === b.eelPfc &&
  a.aesItn === b.aesItn &&
  a.nonDeliveryOption === b.nonDeliveryOption &&
  a.isCertify === b.isCertify &&
  a.certifySigner === b.certifySigner &&
  a.notes === b.notes &&
  a.b13aFilingOption === b.b13aFilingOption &&
  a.b13aNumber === b.b13aNumber &&
  a.eoriNumber === b.eoriNumber &&
  a.taxIdType === b.taxIdType &&
  a.taxIdNumber === b.taxIdNumber;

/**
 * Checks if a zip code is from Northern Ireland.
 * @param zipCode - The zip code to check.
 * @returns True if the zip code is from Northern Ireland, false otherwise.
 */
const isNorthernIrelandZipCode = (zipCode: string): boolean => zipCode.startsWith('BT');

export type CustomsValidationAddress = {
  city: string;
  country?: { iso2: string } | null; // TODO(WEB-5121): used by V1 only
  country_iso2?: null | string;
  phone?: null | string;
  state: string;
  zip: string;
};

export const requiresCustomsDeclaration = (
  a: Maybe<CustomsValidationAddress>,
  b: Maybe<CustomsValidationAddress>,
) => {
  if (!a || !b) return false;

  const aCountryIso2 = a.country?.iso2 ?? a.country_iso2;
  const bCountryIso2 = b.country?.iso2 ?? b.country_iso2;

  if (aCountryIso2 === countries.US.iso2 && bCountryIso2 === countries.US.iso2)
    return isMilitaryAddress(a) || isMilitaryAddress(b) || isUSTerritory(a) || isUSTerritory(b);

  // require custom declarations if shipping from main island UK to Northern Ireland
  if (aCountryIso2 === countries.GB.iso2 && bCountryIso2 === countries.GB.iso2)
    return isNorthernIrelandZipCode(a.zip) && !isNorthernIrelandZipCode(b.zip);

  return aCountryIso2 !== bCountryIso2;
};

const getCustomsPhoneErrors = ({
  addressFrom,
  addressTo,
}: {
  addressFrom?: CustomsValidationAddress | null;
  addressTo?: CustomsValidationAddress | null;
}): { hasPhoneNumberRequiredError: boolean; hasPhoneNumberRequiredWarning: boolean } => {
  const isCrossBorderShipment = requiresCustomsDeclaration(addressFrom, addressTo);
  const hasPhoneNumberRequiredError = isCrossBorderShipment && !addressFrom?.phone;
  const hasPhoneNumberRequiredWarning = isCrossBorderShipment && !addressTo?.phone;

  return {
    hasPhoneNumberRequiredError,
    hasPhoneNumberRequiredWarning,
  };
};

export {
  CustomsDeclaration,
  CustomsDeclarationPayload,
  JSONCustomsDeclaration,
  JSONCustomsDeclarationPayload,
  PIILessJSONCustomsDeclarationPayload,
  fromJSON,
  getCustomsPhoneErrors,
  isDeclarationEqual,
  prepareCustomsDeclarationSegmentData,
  toJSONCustomsDeclarationPayload,
};
export default CustomsDeclaration;
